import { z } from 'zod'
import { Model } from '../../_kernel/architecture'

export type Devices = (typeof ALLOWED_DEVICES)[number]

export const ALLOWED_DEVICES = ['desktop', 'tablet', 'mobile'] as const

export const DeviceValidation = z.object({
  value: z.enum(ALLOWED_DEVICES, { required_error: 'value is required' }),
})

export class Device extends Model {
  static create({ value }: z.infer<typeof DeviceValidation>) {
    DeviceValidation.parse({ value })
    return new Device(value)
  }

  static createOrNull({ value }: z.infer<typeof DeviceValidation>) {
    try {
      return Device.create({ value })
    } catch {
      return null
    }
  }

  static fromJSONOrNull(json?: Partial<ReturnType<Device['toJSON']>>) {
    if (!json?.value) {
      return null
    }
    return Device.create({ value: json.value })
  }

  constructor(private readonly _value?: (typeof ALLOWED_DEVICES)[number]) {
    super()
  }

  get value() {
    return this._value
  }

  isMobile(): this is Device & { value: 'mobile' } {
    return this.isFilled() && this.value === 'mobile'
  }

  isTablet(): this is Device & { value: 'tablet' } {
    return this.isFilled() && this.value === 'tablet'
  }

  isDesktop(): this is Device & { value: 'desktop' } {
    return this.isFilled() && this.value === 'desktop'
  }

  isFilled(): this is Device & z.infer<typeof DeviceValidation> {
    return !!this.value
  }

  is<T extends Devices>(value: T): this is Device & { value: T } {
    return this.isFilled() && this.value === value
  }

  toJSON() {
    return { value: this.value ?? null }
  }
}
